// FILE: apps/web/src/pages/PatientsPage.tsx
// Patient list view. Per ADR-014, full_name is decrypted server-side and
// returned to coordinator/agency_admin roles; lower roles
// receive full_name=null (the API role gate handles the decision — this
// component just displays whatever it gets). The Name column falls back
// to the formatPatientRef cascade when name is null. The Ref column
// always shows the P-XXXXX system slice via formatPatientSlice.
// PII fields other than full_name remain encrypted at rest (ADR-006);
// this page never receives them. Audit logging + bulk-access detection
// are handled server-side (see PatientAccessLog model + nightly cron).

import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { JSX, ReactNode } from "react";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";
import { formatPatientRef, formatPatientSlice, verbalizePatientRef } from "../lib/refs.js";
import { useHighlightedRow } from "../hooks/useHighlightedRow.js";
import { RiskBadge } from "../components/RiskBadge.js";
import { Shell } from "../components/Shell.js";
import { NewPatientModal } from "../components/NewPatientModal.js";
import { downloadCsv } from "../lib/csv-export.js";
import {
  DataTable,
  useDataTable,
  type ColumnDef,
  type ListQuery,
} from "../components/DataTable/index.js";

interface PatientItem {
  _id: string;
  // Optional / nullable per ADR-014 — populated only when the caller's role
  // permits server-side decryption; null/undefined for lower roles. The Name
  // column falls back to formatPatientRef in either case.
  full_name?: string | null;
  age: number;
  biological_sex: string;
  city: string;
  status: string;
  risk_score_band?: string | null;
  enrolment_date?: string;
}

interface PatientListResponse { data: PatientItem[]; total: number; page: number; limit: number; }

const STATUS_OPTIONS = [
  { label: "Active",   value: "active" },
  { label: "Inactive", value: "inactive" },
] as const;

function buildQs(query: ListQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    p.set(k, String(v));
  }
  return p.toString();
}

function usePatients(query: ListQuery): ReturnType<typeof useQuery<PatientListResponse>> {
  const qs = buildQs(query);
  return useQuery<PatientListResponse>({
    queryKey: ["patients", qs],
    queryFn: () => apiFetch<PatientListResponse>(`/patients?${qs}`),
  });
}

function StatusPill({ status }: { status: string }): JSX.Element {
  const active = status === "active";
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
      {status}
    </span>
  );
}

function buildColumns(navigate: ReturnType<typeof useNavigate>): ReadonlyArray<ColumnDef<PatientItem>> {
  return [
    { accessorKey: "full_name", header: "Name",
      cell: (p) => <span className="text-sm font-medium text-slate-900">{formatPatientRef(p)}</span> },
    { accessorKey: "_id", header: "Ref #",
      // Click to copy the full patient UUID — coordinators need it for "Send payment link". The
      // visible text stays the system slice (ADR-014 guarantee); the copy affordance is hover+title.
      cell: (p) => (
        <button type="button"
          onClick={() => { void navigator.clipboard.writeText(p._id).then(() => toast.success("Patient ID copied")); }}
          title={`Copy patient ID ${p._id}`}
          className="font-mono text-xs text-slate-500 hover:text-navy-700 hover:underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded">
          {formatPatientSlice(p)}
        </button>
      ) },
    { accessorKey: "age", header: "Age · Sex",
      cell: (p) => <span className="text-slate-700">{p.age}y · {p.biological_sex.replace("_", " ")}</span> },
    { accessorKey: "city", header: "City",
      cell: (p) => <span className="text-slate-600">{p.city}</span> },
    { accessorKey: "status", header: "Status", sortable: true,
      filterable: true, filterType: "select", filterOptions: STATUS_OPTIONS,
      cell: (p) => <StatusPill status={p.status} /> },
    { accessorKey: "risk_score_band", header: "Risk",
      cell: (p) => <RiskBadge band={p.risk_score_band ?? null} /> },
    { accessorKey: "actions", header: "Actions", align: "right",
      cell: (p) => (
        <button
          type="button"
          onClick={() => navigate(`/patients/${p._id}`)}
          aria-label={`View patient ${verbalizePatientRef(p)}`}
          data-testid={`patients-view-${p._id}-btn`}
          className="text-sm text-navy-800 hover:text-navy-950 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
        >
          View
        </button>
      ),
    },
  ];
}

export function PatientsPage(): JSX.Element {
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);
  const { highlightId } = useHighlightedRow();
  const tableState = useDataTable({
    pageSize: 20,
    defaultSort: { sortBy: "created_at", order: "desc" },
  });
  const columns = useMemo(() => buildColumns(navigate), [navigate]);
  const { data, isLoading, isError, refetch } = usePatients(tableState.query);
  const rows = data?.data ?? [];

  const renderBulkActions = useCallback((ids: readonly string[]): ReactNode => {
    function exportCsv(): void {
      const selected = rows.filter((r) => ids.includes(r._id));
      downloadCsv(`patients-${new Date().toISOString().slice(0, 10)}.csv`, [
        { header: "Name",     value: (r) => formatPatientRef(r) },
        { header: "Ref",      value: (r) => formatPatientSlice(r) },
        { header: "Age",      value: (r) => r.age },
        { header: "Sex",      value: (r) => r.biological_sex },
        { header: "City",     value: (r) => r.city },
        { header: "Status",   value: (r) => r.status },
        { header: "Risk",     value: (r) => r.risk_score_band ?? "" },
        { header: "Enrolled", value: (r) => r.enrolment_date ?? "" },
      ], selected);
      toast.success(`Exported ${selected.length} patient${selected.length === 1 ? "" : "s"}.`);
    }
    return (
      <button type="button" onClick={exportCsv}
        className="text-xs text-navy-800 hover:text-navy-950 underline">Export CSV</button>
    );
  }, [rows]);

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">
            Patients <span className="text-sm font-normal text-slate-400">({data?.total ?? 0})</span>
          </h1>
          <button
            onClick={() => setShowNew(true)}
            data-testid="patients-new-btn"
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors"
          >
            + New Patient
          </button>
        </div>
        <DataTable<PatientItem>
          state={tableState}
          columns={columns}
          data={rows}
          total={data?.total ?? 0}
          rowKey={(p) => p._id}
          ariaLabel="Patients"
          isLoading={isLoading}
          isError={isError}
          errorMessage="Failed to load patients."
          emptyState="No patients yet. Add your first patient."
          searchPlaceholder="Search by name, phone, ref, or city"
          selectable
          bulkActions={renderBulkActions}
          highlightId={highlightId ?? undefined}
        />
      </div>
      {showNew && (
        <NewPatientModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); void refetch(); }}
        />
      )}
    </Shell>
  );
}
